import type { components } from '@/generated/assets.js';
import { authService } from '@/services/auth.js';

export type Location = components['schemas']['Location'];
export type LocationSummary = components['schemas']['LocationSummary'];
export type LocationInput = components['schemas']['LocationInput'];
export type Asset = components['schemas']['Asset'];
export type AssetInput = components['schemas']['AssetInput'];
export type AssetProperties = components['schemas']['AssetProperties'];
export type UserDefinedAssetProperties = components['schemas']['UserDefinedAssetProperties'];
export type AssetType = components['schemas']['AssetType'];
export type AssetTypeProperty = components['schemas']['AssetTypeProperty'];
export type Image = components['schemas']['Image'];
export type PublicAsset = components['schemas']['PublicAsset'];
export type PublicAssetProperty = components['schemas']['PublicAssetProperty'];
export type MaintenanceScheduleItem = components['schemas']['MaintenanceScheduleItem'];
export type MaintenanceItemInput = components['schemas']['MaintenanceItemInput'];
export type MaintenanceItemUpdate = components['schemas']['MaintenanceItemUpdate'];
export type MaintenanceTemplateGroup = components['schemas']['MaintenanceTemplateGroup'];
export type MaintenanceTemplateItem = components['schemas']['MaintenanceTemplateItem'];

class AssetsClient {
    private async _authHeaders(): Promise<Record<string, string>> {
        await authService.ensureValidToken();
        return { Authorization: `Bearer ${authService.getToken()!}` };
    }

    async getLocations(pageToken: string | null = null): Promise<{ locations: LocationSummary[]; nextPageToken: string | null }> {
        const headers: Record<string, string> = await this._authHeaders();
        if (pageToken) headers['X-Page-Token'] = pageToken;

        const res = await fetch('/api/assets/v1/locations', { headers, credentials: 'include' });
        if (!res.ok) throw new Error(`Failed to load locations: ${res.status}`);

        const locations: LocationSummary[] = await res.json();
        return { locations, nextPageToken: res.headers.get('X-Next-Page-Token') };
    }

    async getLocation(locationId: string): Promise<Location> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}`, {
            headers: await this._authHeaders(),
            credentials: 'include'
        });
        if (!res.ok) throw new Error(`Failed to load location: ${res.status}`);
        return res.json();
    }

    async createLocation(payload: FormData): Promise<Location> {
        const res = await fetch('/api/assets/v1/locations', {
            method: 'POST',
            headers: await this._authHeaders(),
            body: payload
        });
        if (!res.ok) throw new Error(`Failed to create location: ${res.status}`);
        return res.json();
    }

    async updateLocation(locationId: string, payload: LocationInput): Promise<Location> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}`, {
            method: 'PUT',
            headers: { ...await this._authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(`Failed to update location: ${res.status}`);
        return res.json();
    }

    async deleteLocation(locationId: string): Promise<void> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}`, {
            method: 'DELETE',
            headers: await this._authHeaders()
        });
        if (!res.ok) throw new Error(`Failed to delete location: ${res.status}`);
    }

    async uploadLocationImages(locationId: string, payload: FormData): Promise<Image[]> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/images`, {
            method: 'POST',
            headers: await this._authHeaders(),
            body: payload
        });
        if (!res.ok) throw new Error(`Failed to upload location images: ${res.status}`);
        return res.json();
    }

    async deleteLocationImage(locationId: string, imageId: string): Promise<void> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/images/${imageId}`, {
            method: 'DELETE',
            headers: await this._authHeaders()
        });
        if (!res.ok) throw new Error(`Failed to delete location image: ${res.status}`);
    }

    async listAssetTypes(pageToken: string | null = null): Promise<{ assetTypes: AssetType[]; nextPageToken: string | null }> {
        const headers: Record<string, string> = await this._authHeaders();
        if (pageToken) headers['X-Page-Token'] = pageToken;

        const res = await fetch('/api/assets/v1/asset-types', { headers });
        if (!res.ok) throw new Error(`Failed to load asset types: ${res.status}`);

        const assetTypes: AssetType[] = await res.json();
        return { assetTypes, nextPageToken: res.headers.get('X-Next-Page-Token') };
    }

    async listAssets(locationId: string, pageToken: string | null = null): Promise<{ assets: Asset[]; nextPageToken: string | null }> {
        const headers: Record<string, string> = await this._authHeaders();
        if (pageToken) headers['X-Page-Token'] = pageToken;

        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets`, { headers });
        if (!res.ok) throw new Error(`Failed to load assets: ${res.status}`);

        const assets: Asset[] = await res.json();
        return { assets, nextPageToken: res.headers.get('X-Next-Page-Token') };
    }

    async getAsset(locationId: string, assetId: string): Promise<Asset> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets/${assetId}`, {
            headers: await this._authHeaders()
        });
        if (!res.ok) throw new Error(`Failed to load asset: ${res.status}`);
        return res.json();
    }

    async createAsset(locationId: string, payload: FormData): Promise<Asset> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets`, {
            method: 'POST',
            headers: await this._authHeaders(),
            body: payload
        });
        if (!res.ok) throw new Error(`Failed to create asset: ${res.status}`);
        return res.json();
    }

    async updateAsset(locationId: string, assetId: string, payload: AssetInput): Promise<Asset> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets/${assetId}`, {
            method: 'PUT',
            headers: { ...await this._authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(`Failed to update asset: ${res.status}`);
        return res.json();
    }

    async deleteAsset(locationId: string, assetId: string): Promise<void> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets/${assetId}`, {
            method: 'DELETE',
            headers: await this._authHeaders()
        });
        if (!res.ok) throw new Error(`Failed to delete asset: ${res.status}`);
    }

    async getPublicAsset(assetId: string): Promise<PublicAsset> {
        const res = await fetch(`/api/assets/v1/public/asset/${assetId}`);
        if (!res.ok) throw new Error(`Failed to load public asset: ${res.status}`);
        return res.json();
    }

    async uploadAssetImages(locationId: string, assetId: string, payload: FormData): Promise<Image[]> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets/${assetId}/images`, {
            method: 'POST',
            headers: await this._authHeaders(),
            body: payload
        });
        if (!res.ok) throw new Error(`Failed to upload asset images: ${res.status}`);
        return res.json();
    }

    async listMaintenanceSchedule(locationId: string, assetId: string): Promise<MaintenanceScheduleItem[]> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets/${assetId}/maintenance`, { headers: await this._authHeaders() });
        if (!res.ok) throw new Error(`Failed to list maintenance schedule: ${res.status}`);
        return res.json();
    }

    async createMaintenanceItem(locationId: string, assetId: string, body: MaintenanceItemInput): Promise<MaintenanceScheduleItem> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets/${assetId}/maintenance`, {
            method: 'POST',
            headers: { ...await this._authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`Failed to create maintenance item: ${res.status}`);
        return res.json();
    }

    async updateMaintenanceItem(locationId: string, assetId: string, itemId: string, body: MaintenanceItemUpdate): Promise<MaintenanceScheduleItem> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets/${assetId}/maintenance/${itemId}`, {
            method: 'PUT',
            headers: { ...await this._authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`Failed to update maintenance item: ${res.status}`);
        return res.json();
    }

    async deleteMaintenanceItem(locationId: string, assetId: string, itemId: string): Promise<void> {
        const res = await fetch(`/api/assets/v1/locations/${locationId}/assets/${assetId}/maintenance/${itemId}`, {
            method: 'DELETE',
            headers: await this._authHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to delete maintenance item: ${res.status}`);
    }

    async listMaintenanceTemplates(): Promise<MaintenanceTemplateGroup[]> {
        const res = await fetch('/api/assets/v1/admin/maintenance-templates', {
            headers: await this._authHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to fetch maintenance templates: ${res.status}`);
        return res.json();
    }

    async upsertAssetLink(assetId: string, nanoId: string): Promise<void> {
        const res = await fetch(`/api/assets/asset-links`, {
            method: 'PUT',
            headers: { ...await this._authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ assetId, nanoId }),
        });
        if (!res.ok) throw new Error(`Failed to upsert asset link: ${res.status}`);
    }
}

export const assetsClient = new AssetsClient();